import React, { useEffect, useState } from "react";
import Logo from "../../../../assets/img/logo.png";
import TelegramIcon from "@mui/icons-material/Telegram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useAuth } from "../../../../contexts/AuthContext";
import { Shell } from "../../../shares/landing/LandingWidgets";

/**
 * Landing navigation.
 *
 * Two things were wrong beyond the styling:
 *  - the mobile <nav> rendered a logo and nothing else. Every link was commented
 *    out, so on a phone the landing page had no navigation at all. The links
 *    below are the same three the desktop bar has, now in a real disclosure.
 *  - the bar scrolled away with the page, so from the middle of a very long
 *    landing page there was no way back to any other section.
 *
 * The desktop navigation shares the dashboards' bordered surface language.
 * Tablets use the mobile disclosure so the navigation never wraps.
 */

const LINKS = [
    { href: "#header", label: "صفحه اصلی" },
    { href: "#courses", label: "دوره‌های آموزشگاه" },
    { href: "#footer", label: "تماس با ما" },
];

function Navbar({ onLoginOpen }) {
    const [open, setOpen] = useState(false);
    const { isLogin } = useAuth();

    useEffect(() => {
        const onResize = () => window.innerWidth >= 1024 && setOpen(false);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    return (
        <header className="landing-nav sticky top-0 z-50 border-b border-rule-subtle bg-canvas/95 backdrop-blur-md">
            <Shell>
                <nav
                    aria-label="ناوبری اصلی"
                    className="flex h-[7.6rem] items-center justify-between gap-xs lg:gap-lg"
                >
                    <a href="#header" className="flex shrink-0 items-center">
                        <img src={Logo} alt="لوگو" className="h-[4.8rem] w-auto" />
                    </a>

                    <ul className="hidden items-center gap-xs rounded-card border border-rule-subtle bg-surface p-2xs text-base text-ink shadow-card lg:flex">
                        {LINKS.map((link) => (
                            <li key={link.href}>
                                <a
                                    href={link.href}
                                    className="landing-navlink block whitespace-nowrap rounded-soft px-md py-xs transition-colors duration-150 hover:bg-primary-subtle hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>

                    <ul className="hidden items-center gap-md border-s border-rule-subtle ps-lg text-base text-ink-muted lg:flex">
                        <li className="text-sm">ما را دنبال کنید</li>
                        <li aria-hidden="true" className="inline-flex text-primary">
                            <TelegramIcon sx={{ fontSize: 20 }} />
                        </li>
                        <li aria-hidden="true" className="inline-flex text-primary">
                            <LinkedInIcon sx={{ fontSize: 20 }} />
                        </li>
                    </ul>

                    <div className="ms-auto flex items-center gap-xs lg:hidden">
                        {!isLogin && (
                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    onLoginOpen();
                                }}
                                className="landing-cta inline-flex h-[4.4rem] items-center justify-center whitespace-nowrap rounded-soft bg-primary px-sm text-base font-medium text-ink-inverse shadow-card transition-colors duration-150 hover:bg-primary-hover active:bg-primary-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none"
                            >
                                ورود
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setOpen((v) => !v)}
                            aria-expanded={open}
                            aria-controls="landing-mobile-nav"
                            aria-label={open ? "بستن منو" : "باز کردن منو"}
                            className="flex h-[4.4rem] w-[4.4rem] items-center justify-center rounded-soft border border-rule-subtle bg-surface text-ink-muted shadow-card transition-colors duration-150 hover:bg-primary-subtle hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none"
                        >
                            {open ? (
                                <CloseRoundedIcon sx={{ fontSize: 24 }} />
                            ) : (
                                <MenuRoundedIcon sx={{ fontSize: 24 }} />
                            )}
                        </button>
                    </div>
                </nav>

                <div
                    id="landing-mobile-nav"
                    className={`overflow-hidden transition-all duration-200 lg:hidden motion-reduce:transition-none ${
                        open ? "max-h-[28rem] opacity-100" : "max-h-0 opacity-0"
                    }`}
                >
                    <ul className="mb-md flex flex-col gap-2xs rounded-panel border border-rule-subtle bg-surface p-sm shadow-card">
                        {LINKS.map((link) => (
                            <li key={link.href}>
                                <a
                                    href={link.href}
                                    onClick={() => setOpen(false)}
                                    className="block rounded-soft px-md py-sm text-base text-ink transition-colors duration-150 hover:bg-surface-sunken hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                        <li className="flex items-center gap-md px-md pt-sm text-sm text-ink-muted">
                            <span>ما را دنبال کنید</span>
                            <span className="inline-flex cursor-pointer hover:text-primary">
                                <TelegramIcon sx={{ fontSize: 20 }} />
                            </span>
                            <span className="inline-flex cursor-pointer hover:text-primary">
                                <LinkedInIcon sx={{ fontSize: 20 }} />
                            </span>
                        </li>
                    </ul>
                </div>
            </Shell>
        </header>
    );
}

export default Navbar;
